import {useDispatch, useSelector} from "react-redux";
import {Cards} from "../Cards/Cards";
import {MenuLists} from "./MenuLists";
import {CardForm} from "../Cards/CardForm";
import {useEffect, useMemo, useState} from "react";
import {RenameBoardListAPI} from "../../../../../store/api/get/Board/Board";
import {
    DndContext,
    useSensor,
    useSensors,
    DragOverlay,
    MeasuringStrategy,
    TouchSensor,
    MouseSensor,
} from "@dnd-kit/core";
import {
    SortableContext,
    horizontalListSortingStrategy,
} from "@dnd-kit/sortable";

import {SortableList} from "../../../../../components/Sortable/SortableList";
import {handleDragEnd} from "../../../../../hooks/handleDragEnd";
import {boardCollisionDetection} from "../../../../../hooks/boardCollisionDetection";
import {CardLabels} from "../Cards/CardLabels";
import {RENAME_LIST, SET_BOARD_LISTS} from "../../../../../store/constants";
import {SOCKET} from "../../../../../URL";

export const Lists = ({
                          setOpenListMenuId,
                          openListMenuId,
                          handleOpenActionModal,
                          activeListId,
                          setActiveListId,
                          handleCreateCard,
                          cardTitle,
                          setCardTitle,
                      }) => {
    const dispatch = useDispatch();
    const activeBoard = useSelector((state) => state.boards.activeBoard);
    const lists = useSelector((state) => state.boards.boardLists || []);
    const [editingListId, setEditingListId] = useState(null);
    const user = useSelector(state => state.user.user);

    const userRole = activeBoard?.rbac?.find(
        (el) => el.user_id === user?.id
    )?.role;
    const [activeDragItem, setActiveDragItem] = useState(null);
    const [overListId, setOverListId] = useState(null);
    const [overCardId, setOverCardId] = useState(null);

    const visibleLists = lists.filter((el) => !el.is_archive);

    // Թարմացված sensors - աջակցություն mobile-ի համար
    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 4,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 200,
                tolerance: 5,
            },
        })
    );

    const handleChangeListTitle = (e, list) => {
        if (e.key === "Enter") {
            const newName = e.target.value.trim();
            setEditingListId(null);

            if (newName && newName !== list?.title) {
                dispatch(RenameBoardListAPI(list?.id, activeBoard?.id, newName));
            }
        }

        if (e.key === "Escape") {
            setEditingListId(null);
        }
    };

    useEffect(() => {
        const handleChangeListTitle = (data) => {
            if (String(data.board_id) !== String(activeBoard?.id)) return;

            dispatch({
                type: RENAME_LIST,
                payload: data.list
            });
        };

        SOCKET.on("rename_list", handleChangeListTitle);

        return () => {
            SOCKET.off("rename_list", handleChangeListTitle);
        };
    }, [dispatch, activeBoard?.id]);

    const findListByCardId = (boardLists, cardId) => {
        return boardLists.find((list) =>
            (list.cards || []).some((card) => card.id === cardId)
        );
    };

    const updateCardSorts = (cards) => {
        return cards.map((card, index) => ({
            ...card,
            sort: index + 1,
        }));
    };

    const allCards = useMemo(() => {
        return visibleLists.flatMap((list) => list.cards || []);
    }, [visibleLists]);

    const handleDragStartLocal = (event) => {
        const {active} = event;
        const activeId = String(active.id);

        if (activeId.startsWith("card-")) {
            const cardId = Number(activeId.replace("card-", ""));
            const card = allCards.find((item) => item.id === cardId);

            if (card) {
                setActiveDragItem({
                    type: "card",
                    data: card,
                });
            }
        }

        if (activeId.startsWith("list-")) {
            const listId = Number(activeId.replace("list-", ""));
            const list = visibleLists.find((item) => item.id === listId);

            if (list) {
                setActiveDragItem({
                    type: "list",
                    data: list,
                });
            }
        }
    };

    const handleDragOverLocal = (event) => {
        const {over} = event;

        if (!over) {
            setOverListId(null);
            setOverCardId(null);
            return;
        }

        const overId = String(over.id);

        if (overId.startsWith("list-")) {
            setOverListId(Number(overId.replace("list-", "")));
            setOverCardId(null);
            return;
        }

        if (overId.startsWith("card-")) {
            const cardId = Number(overId.replace("card-", ""));
            const targetList = findListByCardId(visibleLists, cardId);

            setOverCardId(cardId);
            setOverListId(targetList?.id || null);
        }
    };

    const handleDragEndLocal = async (event) => {
        setActiveDragItem(null);
        setOverListId(null);
        setOverCardId(null);
        if (userRole !== 1) {
            try {
                await handleDragEnd(
                    event,
                    lists,
                    dispatch,
                    findListByCardId,
                    updateCardSorts,
                    activeBoard?.id
                );
            } catch (error) {
                alert(error.message || "Could not save the move. Please try again.");
            }
        }
    };

    const handleDragCancelLocal = () => {
        setActiveDragItem(null);
        setOverListId(null);
        setOverCardId(null);
    };

    useEffect(() => {
        const handleMoveList = (data) => {
            if (String(data?.board_id) !== String(activeBoard?.id) || !Array.isArray(data?.lists)) return;
            dispatch({
                type: SET_BOARD_LISTS,
                payload: data.lists,
            });
        };

        SOCKET.on("move_list", handleMoveList);

        return () => {
            SOCKET.off("move_list", handleMoveList);
        };
    }, [activeBoard?.id, dispatch]);

    useEffect(() => {
        const handleMoveCard = (data) => {
            if (String(data?.board_id) !== String(activeBoard?.id) || !Array.isArray(data?.lists)) return;
            dispatch({type: SET_BOARD_LISTS, payload: data.lists})
        }

        SOCKET.on("move_card", handleMoveCard);

        return () => {
            SOCKET.off("move_card", handleMoveCard);
        };
    }, [activeBoard?.id, dispatch]);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={boardCollisionDetection}
            onDragStart={handleDragStartLocal}
            onDragOver={handleDragOverLocal}
            onDragEnd={handleDragEndLocal}
            onDragCancel={handleDragCancelLocal}
            autoScroll={{
                enabled: true,
                threshold: {
                    x: 0.2,
                    y: 0.2,
                },
            }}
            measuring={{
                droppable: {
                    strategy: MeasuringStrategy.Always,
                },
            }}
        >
            <SortableContext
                items={visibleLists.map((list) => `list-${list.id}`)}
                strategy={horizontalListSortingStrategy}
            >
                {visibleLists.map((list) => (
                    <SortableList
                        key={list.id}
                        list={list}
                        isActiveDrop={overListId === list.id}
                        editingListId={editingListId}
                        disabled={userRole === 1}
                    >
                        {({dragHandleProps}) => (
                            <div className="boardList">
                                <div className="boardList__header" {...dragHandleProps}>
                                    {editingListId !== list.id ? (
                                        <h3 onClick={() => userRole !== 1 && setEditingListId(list.id)}>
                                            {list?.title}
                                        </h3>
                                    ) : (
                                        <input
                                            className="boardTitleInput"
                                            type="text"
                                            autoFocus
                                            defaultValue={list?.title}
                                            onBlur={() => setEditingListId(null)}
                                            onKeyDown={(e) => handleChangeListTitle(e, list)}
                                            style={{width: "100%"}}
                                        />
                                    )}

                                    <div className="boardList__menuWrap">
                                        <button
                                            type="button"
                                            className="boardList__menuBtn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenListMenuId((prev) =>
                                                    prev === list.id ? null : list.id
                                                );
                                            }}
                                        >
                                            ⋮
                                        </button>

                                        <MenuLists
                                            openListMenuId={openListMenuId}
                                            list={list}
                                            handleOpenActionModal={handleOpenActionModal}
                                        />
                                    </div>
                                </div>

                                <Cards list={list} overCardId={overCardId}/>

                                {activeListId !== list.id ? (
                                    <button
                                        type="button"
                                        className="boardList__addCard"
                                        onClick={() => userRole !== 1 && setActiveListId(list.id)}
                                    >
                                        + Add a card
                                    </button>
                                ) : (
                                    <CardForm
                                        handleCreateCard={handleCreateCard}
                                        cardTitle={cardTitle}
                                        setCardTitle={setCardTitle}
                                        list={list}
                                        setActiveListId={setActiveListId}
                                    />
                                )}
                            </div>
                        )}
                    </SortableList>
                ))}
            </SortableContext>

            <DragOverlay>
                {activeDragItem?.type === "card" ? (
                    <div className="boardCard boardCard--overlay">
                        <CardLabels card={activeDragItem.data}/>
                        {activeDragItem.data.name}
                    </div>
                ) : activeDragItem?.type === "list" ? (
                    <div className="boardList boardList--overlay">
                        <div className="boardList__header">
                            <h3>{activeDragItem.data.title}</h3>
                        </div>
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    );
};
